import { apiClient } from './apiClient';

export interface IptvProvider {
  id: string;
  name: string;
  endpoint: string;
  proxyEnabled?: boolean;
  username?: string | null;
  notes?: string | null;
  activeCons?: number | null;
  maxConnections?: number | null;
  expDate?: number | null;
  createdDate?: string;
  updatedDate?: string;
}

export interface IptvProviderChannel {
  id: string;
  title: string;
  logo: string | null;
  group: string;
  url: string;
}

interface IptvProviderPayload {
  name: string;
  endpoint: string;
  proxyEnabled?: boolean;
  username?: string;
  notes?: string;
}

export interface CreateIptvProviderRequest extends IptvProviderPayload {
  password?: string;
}

export interface UpdateIptvProviderRequest extends IptvProviderPayload {
  id: string;
  password?: string;
}

export interface IptvProvidersResponse {
  success: boolean;
  providers: IptvProvider[];
}

export interface IptvProviderResponse {
  success: boolean;
  message?: string;
  provider: IptvProvider;
}

export const iptvProviderService = {
  async getProviders(): Promise<IptvProvidersResponse> {
    return apiClient.get<IptvProvidersResponse>('/iptv-providers');
  },

  async createProvider(request: CreateIptvProviderRequest): Promise<IptvProviderResponse> {
    return apiClient.post<IptvProviderResponse>('/iptv-providers', request);
  },

  async updateProvider(request: UpdateIptvProviderRequest): Promise<IptvProviderResponse> {
    return apiClient.put<IptvProviderResponse>(`/iptv-providers/${request.id}`, request);
  },

  async deleteProvider(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(`/iptv-providers/${id}`);
  },

  async getProviderChannels(provider: IptvProvider): Promise<IptvProviderChannel[]> {
    if (!provider.id) {
      throw new Error('Proveedor no encontrado');
    }

    const response = await apiClient.getInstance().get<string>(`/iptv-providers/${provider.id}/playlist`, {
      responseType: 'text',
      headers: {
        Accept: 'text/plain, */*',
      },
    });

    const playlist = response.data;
    return parseM3uPlaylist(playlist);
  },
};

const parseM3uPlaylist = (playlist: string): IptvProviderChannel[] => {
  const lines = playlist
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const channels: IptvProviderChannel[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (!line.startsWith('#EXTINF')) {
      continue;
    }

    const attributes = parseExtInfAttributes(line);
    const title = extractExtInfTitle(line, attributes.tvgName);

    channels.push({
      id: `${title}-${channels.length}`,
      title,
      logo: normalizePlaylistValue(attributes.tvgLogo),
      group: normalizePlaylistValue(attributes.groupTitle) ?? 'Sin grupo',
      url: extractChannelUrl(lines, index + 1),
    });
  }

  return channels;
};

const parseExtInfAttributes = (line: string) => {
  const attributePattern = /([\w-]+)="([^"]*)"/g;
  const attributes: Record<string, string> = {};

  for (const match of line.matchAll(attributePattern)) {
    const key = toCamelCase(match[1]);
    attributes[key] = match[2];
  }

  return attributes as Record<'tvgName' | 'tvgLogo' | 'groupTitle', string | undefined>;
};

const extractExtInfTitle = (line: string, fallbackTitle?: string) => {
  const commaIndex = line.lastIndexOf(',');
  const titleFromLine = commaIndex >= 0 ? line.slice(commaIndex + 1).trim() : '';
  return titleFromLine || normalizePlaylistValue(fallbackTitle) || 'Sin titulo';
};

const normalizePlaylistValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const extractChannelUrl = (lines: string[], startIndex: number) => {
  for (let index = startIndex; index < lines.length; index += 1) {
    const trimmed = lines[index]?.trim();

    if (!trimmed) {
      continue;
    }

    if (trimmed.startsWith('#EXTINF')) {
      return '';
    }

    if (!trimmed.startsWith('#')) {
      return trimmed;
    }
  }

  return '';
};

const toCamelCase = (value: string) =>
  value.replace(/-([a-z])/g, (_match, letter: string) => letter.toUpperCase());
